import { useMemo } from "react";

import urls from "@/constants/Urls";
import Avatar from "@/design/components/Avatar";
import useAudio from "@/utils/hooks/useAudio";
import useColor from "@/utils/hooks/useColor";
import Valkyrie, { viArrowRotateRight, viCircleExclamation, viMusic, viPause, viPlay } from "@sippy-platform/valkyrie";

import { Box, Card, IconButton, List, ListItem, ListItemAvatar, ListItemText, Slider } from "@mui/material";
import PropTypes from "prop-types";

function SystemSound({ entry }) {
  const { background } = useColor(entry.name);

  const src = useMemo(
    () => `${urls.audio}${encodeURIComponent(entry.directory)}.wav?alt=media&token=${entry.token}`,
    [entry.directory, entry.token],
  );

  const { playable, duration, playPause, progress, secondsToTime, seek, seekFinish, status } = useAudio(src);
  return (
    <Card
      variant="outlined"
      sx={(theme) => ({
        border: "none",
        position: "relative",
        contain: "paint",
        "&:nth-of-type(odd)": {
          backgroundColor: "#f8f8f8",
          ...theme.applyStyles("dark", {
            backgroundColor: "#28282d",
          }),
        },
        "&:hover": {
          backgroundColor: "#efefef",
          ...theme.applyStyles("dark", {
            backgroundColor: "#323237",
          }),
        },
      })}
    >
      <List>
        <ListItem
          secondaryAction={
            <IconButton onClick={playPause} disabled={!playable} color="inherit">
              <Valkyrie
                icon={
                  status === "stopped"
                    ? viArrowRotateRight
                    : status === "paused"
                      ? viPlay
                      : status === "error"
                        ? viCircleExclamation
                        : viPause
                }
              />
            </IconButton>
          }
        >
          <ListItemAvatar>
            <Avatar text={entry.name} icon={viMusic} />
          </ListItemAvatar>
          <ListItemText
            primary={entry.name}
            secondary={
              <>
                {secondsToTime(progress)}
                <Box as="span" sx={{ opacity: 0.75 }}>
                  {" "}
                  / {secondsToTime(duration)}
                </Box>
              </>
            }
          />
        </ListItem>
      </List>
      <Slider
        sx={{
          position: "absolute",
          bottom: 0,
          left: 0,
          width: "100%",
          backgroundColor: "transparent",
          transition: "all .1s ease-in",
          height: 4,
          p: 0,
          "&:hover": {
            backgroundColor: background[100],
          },
          ".MuiSlider-rail": {
            height: 4,
            opacity: 0,
          },
          ".MuiSlider-track": {
            height: 4,
            backgroundColor: background[500],
          },
          ".MuiSlider-thumb": {
            display: "none",
          },
        }}
        value={progress}
        onChange={(e, value) => seek(value)}
        onChangeCommitted={seekFinish}
        min={0}
        max={duration}
        disabled={!playable}
      />
    </Card>
  );
}

SystemSound.propTypes = {
  entry: PropTypes.object,
};

export default SystemSound;
